import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { baixarMidia } from './baixar-midia.js';
/** Até onde a foto amplia. Oito vezes já lê etiqueta de equipamento em foto de celular. */
const ZOOM_MAXIMO = 8;
const INICIAL = { escala: 1, x: 0, y: 0 };
/**
 * Foto e vídeo abertos por cima da conversa, sem sair dela.
 *
 * Antes o clique levava para outra aba: quem estava atendendo perdia o fio de vista, e o
 * cliente no celular saía do chat para ver a própria foto e tinha de voltar pelo botão de
 * retorno. A conversa é o lugar; a mídia se abre em cima dela e some do mesmo jeito.
 *
 * Fecha de três formas — Esc, clique no fundo e o botão —, porque cada pessoa tenta uma
 * primeiro. O clique só conta quando é no fundo: arrastar para ampliar a imagem e soltar
 * fora dela fechava a janela no meio do gesto.
 */
export function VisualizadorMidia({ url, nomeOriginal, mimeType, aoFechar, perguntarOndeSalvar = false, }) {
    const [baixando, setBaixando] = useState(false);
    const [falhou, setFalhou] = useState(false);
    /**
     * Quanto a foto já foi girada, em graus.
     *
     * Foto de celular chega deitada o tempo todo — telhado, poste, etiqueta de equipamento —
     * e o atendente precisava virar a cabeça para ler (pedido de 29/09). Cada clique gira
     * mais um quarto de volta; fechar e abrir de novo volta ao original, que é o esperado de
     * um ajuste de visualização (o arquivo não muda).
     */
    const [graus, setGraus] = useState(0);
    /**
     * O zoom (pedido de 30/09).
     *
     * Os atendentes tentaram de tudo para ampliar o print que o cliente manda — nome de rede,
     * número de série, mensagem de erro na tela do roteador — e não havia como: a foto abria
     * no tamanho da janela e parava ali. Agora a roda do mouse amplia no ponto onde o cursor
     * está, arrastar move, e o duplo clique vai e volta.
     */
    const [enquadramento, setEnquadramento] = useState(INICIAL);
    const palco = useRef(null);
    const arrasto = useRef(null);
    /** Distância entre os dois dedos no gesto de pinça, para saber se abriu ou fechou. */
    const pinca = useRef(null);
    const video = mimeType.startsWith('video/');
    const ampliada = enquadramento.escala > 1;
    /**
     * Amplia mantendo parado o ponto que está sob o cursor (ou entre os dedos).
     *
     * É o que faz o zoom parecer natural: quem aponta para o canto da etiqueta e gira a roda
     * espera que a etiqueta cresça ali, não que a foto fuja para o meio da tela.
     */
    const ampliar = useCallback((fator, focoX, focoY) => {
        const caixa = palco.current?.getBoundingClientRect();
        setEnquadramento((atual) => {
            const escala = Math.min(ZOOM_MAXIMO, Math.max(1, atual.escala * fator));
            if (escala === 1)
                return INICIAL;
            if (!caixa || focoX === undefined || focoY === undefined) {
                return { ...atual, escala };
            }
            const px = focoX - (caixa.left + caixa.width / 2);
            const py = focoY - (caixa.top + caixa.height / 2);
            const k = escala / atual.escala;
            return { escala, x: px - k * (px - atual.x), y: py - k * (py - atual.y) };
        });
    }, []);
    useEffect(() => {
        const aoTeclar = (e) => {
            if (e.key === 'Escape')
                aoFechar();
            if (e.key === '+' || e.key === '=')
                ampliar(1.3);
            if (e.key === '-')
                ampliar(1 / 1.3);
            if (e.key === '0')
                setEnquadramento(INICIAL);
        };
        document.addEventListener('keydown', aoTeclar);
        // Sem isto a conversa rola atrás do visualizador no celular, e ao fechar a pessoa está
        // em outro ponto do histórico sem ter pedido.
        const overflowAnterior = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', aoTeclar);
            document.body.style.overflow = overflowAnterior;
        };
    }, [aoFechar, ampliar]);
    /**
     * A roda do mouse precisa de um ouvinte à moda antiga.
     *
     * O React registra `onWheel` como ouvinte passivo na raiz, e ouvinte passivo não pode
     * chamar `preventDefault` — sem isso, girar a roda ampliava a foto E rolava a página
     * atrás dela.
     */
    useEffect(() => {
        const alvo = palco.current;
        if (!alvo || video)
            return;
        const aoGirar = (e) => {
            e.preventDefault();
            ampliar(Math.exp(-e.deltaY * 0.0015), e.clientX, e.clientY);
        };
        alvo.addEventListener('wheel', aoGirar, { passive: false });
        return () => alvo.removeEventListener('wheel', aoGirar);
    }, [ampliar, video]);
    /** Girar reenquadra: a foto deitada e ampliada ficaria fora da tela no novo eixo. */
    function girar() {
        setGraus((g) => (g + 90) % 360);
        setEnquadramento(INICIAL);
    }
    const deitada = graus % 180 !== 0;
    return (_jsxs("div", { role: "dialog", "aria-modal": "true", "aria-label": nomeOriginal, onClick: (e) => {
            if (e.target === e.currentTarget)
                aoFechar();
        }, className: "fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/90 p-4", children: [_jsxs("div", { className: "absolute inset-x-0 top-0 z-10 flex items-center gap-3 bg-gradient-to-b from-black/70 to-transparent p-3 text-white", children: [_jsx("span", { className: "min-w-0 flex-1 truncate text-sm", children: nomeOriginal }), falhou && (_jsx("span", { className: "text-xs text-amber-300", children: "N\u00E3o consegui baixar. Tente de novo." })), !video && (_jsxs(_Fragment, { children: [_jsx("button", { type: "button", onClick: (e) => {
                                    e.stopPropagation();
                                    ampliar(1 / 1.4);
                                }, disabled: !ampliada, title: "Diminuir", "aria-label": "Diminuir", className: "rounded p-1.5 hover:bg-white/20 disabled:opacity-40", children: _jsxs("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", "aria-hidden": "true", children: [_jsx("circle", { cx: "11", cy: "11", r: "7" }), _jsx("path", { d: "M8 11h6M20 20l-3.5-3.5" })] }) }), _jsxs("button", { type: "button", onClick: (e) => {
                                    e.stopPropagation();
                                    setEnquadramento(INICIAL);
                                }, title: "Tamanho original", "aria-label": "Tamanho original", className: "min-w-12 rounded px-1 py-1.5 text-xs tabular-nums hover:bg-white/20", children: [Math.round(enquadramento.escala * 100), "%"] }), _jsx("button", { type: "button", onClick: (e) => {
                                    e.stopPropagation();
                                    ampliar(1.4);
                                }, title: "Ampliar", "aria-label": "Ampliar", className: "rounded p-1.5 hover:bg-white/20", children: _jsxs("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", "aria-hidden": "true", children: [_jsx("circle", { cx: "11", cy: "11", r: "7" }), _jsx("path", { d: "M8 11h6M11 8v6M20 20l-3.5-3.5" })] }) }), _jsx("button", { type: "button", onClick: (e) => {
                                    e.stopPropagation();
                                    girar();
                                }, title: "Girar 90\u00B0", "aria-label": "Girar 90 graus", className: "rounded p-1.5 hover:bg-white/20", children: _jsxs("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", children: [_jsx("path", { d: "M21 12a9 9 0 1 1-3.5-7.1" }), _jsx("path", { d: "M21 3v6h-6" })] }) })] })), _jsx("button", { type: "button", disabled: baixando, onClick: (e) => {
                            e.stopPropagation();
                            setBaixando(true);
                            setFalhou(false);
                            void baixarMidia(url, nomeOriginal, { perguntarOnde: perguntarOndeSalvar })
                                .then((ok) => setFalhou(!ok))
                                .finally(() => setBaixando(false));
                        }, title: perguntarOndeSalvar ? 'Salvar' : 'Baixar', "aria-label": perguntarOndeSalvar ? 'Salvar' : 'Baixar', className: "rounded p-1.5 hover:bg-white/20 disabled:opacity-50", children: _jsx("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", children: _jsx("path", { d: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" }) }) }), _jsx("button", { type: "button", onClick: aoFechar, title: "Fechar", "aria-label": "Fechar", className: "rounded p-1.5 hover:bg-white/20", children: _jsx("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", "aria-hidden": "true", children: _jsx("path", { d: "M18 6 6 18M6 6l12 12" }) }) })] }), video ? (
            // `autoPlay` porque quem clicou num vídeo quer vê-lo; `controls` para poder parar.
            _jsx("video", { src: url, controls: true, autoPlay: true, className: "max-h-[85vh] max-w-full rounded", onClick: (e) => e.stopPropagation() })) : (_jsx("div", { ref: palco, 
                // O palco ocupa a janela toda para a roda do mouse funcionar em qualquer ponto,
                // e `overflow-hidden` segura a foto ampliada dentro dele.
                className: "flex h-full w-full items-center justify-center overflow-hidden", onClick: (e) => {
                    // Fechar no fundo continua valendo — menos quando a foto está ampliada, em que
                    // o clique faz parte de arrastar.
                    if (e.target === e.currentTarget && !ampliada)
                        aoFechar();
                }, onDoubleClick: (e) => {
                    e.stopPropagation();
                    if (ampliada)
                        setEnquadramento(INICIAL);
                    else
                        ampliar(2.5, e.clientX, e.clientY);
                }, onPointerDown: (e) => {
                    if (!ampliada || e.pointerType === 'touch')
                        return;
                    arrasto.current = {
                        x: e.clientX,
                        y: e.clientY,
                        origemX: enquadramento.x,
                        origemY: enquadramento.y,
                    };
                    e.currentTarget.setPointerCapture(e.pointerId);
                }, onPointerMove: (e) => {
                    const inicio = arrasto.current;
                    if (!inicio)
                        return;
                    setEnquadramento((atual) => ({
                        ...atual,
                        x: inicio.origemX + (e.clientX - inicio.x),
                        y: inicio.origemY + (e.clientY - inicio.y),
                    }));
                }, onPointerUp: () => {
                    arrasto.current = null;
                }, onPointerCancel: () => {
                    arrasto.current = null;
                }, 
                // Pinça no celular: dois dedos. O `touchAction: none` é o que impede o navegador
                // de assumir o gesto e dar zoom na página inteira em vez de na foto.
                onTouchStart: (e) => {
                    if (e.touches.length === 2)
                        pinca.current = distancia(e.touches[0], e.touches[1]);
                }, onTouchMove: (e) => {
                    if (e.touches.length !== 2 || pinca.current === null)
                        return;
                    const agora = distancia(e.touches[0], e.touches[1]);
                    const meioX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
                    const meioY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
                    ampliar(agora / pinca.current, meioX, meioY);
                    pinca.current = agora;
                }, onTouchEnd: () => {
                    pinca.current = null;
                }, style: { touchAction: 'none' }, children: _jsx("img", { src: url, alt: nomeOriginal, draggable: false, 
                    // Deitada, a foto ocupa a largura da tela com a altura dela e vice-versa: os
                    // limites trocam de lado junto com a rotação, senão uma foto girada estoura a
                    // janela e fica com as pontas cortadas.
                    style: {
                        transform: `translate(${enquadramento.x}px, ${enquadramento.y}px) scale(${enquadramento.escala}) rotate(${graus}deg)`,
                        maxHeight: deitada ? '90vw' : '85vh',
                        maxWidth: deitada ? '85vh' : '90vw',
                        cursor: ampliada ? 'grab' : 'zoom-in',
                    }, className: "rounded object-contain select-none", onClick: (e) => e.stopPropagation() }) })), !video && (_jsx("p", { className: "absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3 text-center text-[11px] text-white/70", children: "Role a roda do mouse para ampliar \u00B7 duplo clique vai e volta \u00B7 arraste para mover" }))] }));
}
/** Distância entre dois dedos, para medir a pinça. */
function distancia(a, b) {
    return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}
//# sourceMappingURL=VisualizadorMidia.js.map